import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, Building2, Mail, Phone, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import ConfirmDelete from "@/components/ConfirmDelete";
import { useAuth } from "@/context/AuthContext";
import { IfCan } from "@/components/PermGate";
import api, { formatApiError } from "@/lib/api";
import ClientForm from "@/components/ClientForm";

export default function Clients() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = async (q = "") => {
    setLoading(true);
    try {
      const res = await api.get(`/clients${q ? `?search=${encodeURIComponent(q)}` : ""}`);
      setClients(res.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const t = setTimeout(() => load(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const onCreated = (client) => {
    setClients((prev) => [client, ...prev]);
    setOpen(false);
    toast.success("Client created");
  };

  return (
    <div data-testid="clients-page">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-6">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Directory</div>
          <h1 className="font-heading text-2xl md:text-3xl font-semibold text-slate-900">Clients</h1>
          <p className="text-slate-500 text-sm mt-1 hidden md:block">All company accounts and their sites.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <IfCan module="clients" level="edit"><DialogTrigger asChild>
            <Button className="bg-indigo-600 hover:bg-indigo-700 w-full md:w-auto" data-testid="new-client-btn">
              <Plus className="w-4 h-4 mr-1.5" /> New client
            </Button>
          </DialogTrigger></IfCan>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create client</DialogTitle>
            </DialogHeader>
            <ClientForm onSuccess={onCreated} onCancel={() => setOpen(false)} />
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="p-4 border-b border-slate-200">
          <div className="relative md:max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search by name, contact, email, phone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="client-search-input"
            />
          </div>
        </div>
        {/* Desktop table */}
        <div className="hidden md:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Company</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Contact</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Email</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Phone</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Sites</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">GST</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right w-24">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">Loading...</TableCell></TableRow>
              ) : clients.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-10" data-testid="clients-empty">
                  <Building2 className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                  No clients yet. Create your first one.
                </TableCell></TableRow>
              ) : (
                clients.map((c) => (
                  <TableRow key={c.id} className="hover:bg-slate-50" data-testid={`client-row-${c.id}`}>
                    <TableCell className="font-medium">
                      <Link to={`/clients/${c.id}`} className="text-slate-900 hover:text-indigo-600" data-testid={`client-name-${c.id}`}>
                        {c.company_name}
                      </Link>
                    </TableCell>
                    <TableCell className="text-slate-600">{c.contact_person}</TableCell>
                    <TableCell className="text-slate-600 text-sm">{c.email}</TableCell>
                    <TableCell className="text-slate-600 text-sm">{c.phone}</TableCell>
                    <TableCell className="text-slate-600 text-sm">{(c.shipping_addresses || []).length}</TableCell>
                    <TableCell className="text-slate-500 text-xs font-mono">{c.gst_number}</TableCell>
                    <TableCell className="text-right">
                      {isAdmin && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-slate-400 hover:text-rose-600 hover:border-rose-200"
                          onClick={() => setDeleteTarget(c)}
                          data-testid={`client-delete-btn-${c.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        {/* Mobile cards */}
        <ul className="md:hidden divide-y divide-slate-100">
          {loading ? (
            <li className="text-center text-slate-400 py-8">Loading...</li>
          ) : clients.length === 0 ? (
            <li className="text-center text-slate-400 py-10" data-testid="clients-empty-m">
              <Building2 className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              No clients yet. Create your first one.
            </li>
          ) : clients.map((c) => (
            <li key={c.id} data-testid={`client-card-${c.id}`} className="relative">
              <Link to={`/clients/${c.id}`} className="block p-4 active:bg-slate-50" data-testid={`client-name-m-${c.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-900 truncate">{c.company_name}</div>
                    <div className="text-sm text-slate-600 truncate">{c.contact_person}</div>
                  </div>
                  <span className="text-[10px] text-slate-500 font-mono shrink-0">{(c.shipping_addresses || []).length} site{(c.shipping_addresses || []).length === 1 ? "" : "s"}</span>
                </div>
                <div className="mt-2 space-y-0.5 text-xs text-slate-500">
                  {c.email && <div className="truncate"><Mail className="w-3 h-3 inline mr-1" />{c.email}</div>}
                  {c.phone && <div className="truncate"><Phone className="w-3 h-3 inline mr-1" />{c.phone}</div>}
                  {c.gst_number && <div className="font-mono truncate">GST · {c.gst_number}</div>}
                </div>
              </Link>
              {isAdmin && (
                <button
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDeleteTarget(c); }}
                  className="absolute top-3 right-3 text-slate-300 hover:text-rose-600 p-1"
                  data-testid={`client-delete-btn-m-${c.id}`}
                  aria-label="Delete client"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </li>
          ))}
        </ul>
      </div>

      <ConfirmDelete
        open={!!deleteTarget}
        onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}
        title="Delete this client?"
        recordLabel={deleteTarget?.company_name || ""}
        softDelete
        loading={deleting}
        confirmLabel="Archive client"
        testIdPrefix="client-confirm-delete"
        onConfirm={async () => {
          if (!deleteTarget) return;
          setDeleting(true);
          try {
            await api.delete(`/clients/${deleteTarget.id}`);
            toast.success("Client archived — restore anytime from the Audit Log");
            setDeleteTarget(null);
            load(search);
          } catch (e) { toast.error(formatApiError(e)); }
          finally { setDeleting(false); }
        }}
      />
    </div>
  );
}
